
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';

import { WidgetsActions } from '@actions/index'
import * as fromWidgets from '@reducers/index'
import { Observable } from 'rxjs';
import { ReviewModel, RequestModel, WidgetModel, ReviewRequestFilteredPaginatedModel, ReviewPageRequestDescriptor } from '@models/index';

/**
 * WidgetsFacadeService dispatches all needed actions
 * related to widgets and gets all the app storage 
 * information asociated with the respective selectors.
 */
@Injectable({
    providedIn: 'root'
})

export class WidgetsFacadeService {

    /**
     * WidgetsFacadeService Constructor
     * @param store App storage
     * @param router App router
     */
    constructor(private store: Store<any>, public router: Router, ) { }

    /**
     * Reviews by location getter.
     * Gets all the reviews associated to
     * a particular location.
     * @returns An observable of a ReviewModel
     * array that contains all the reviews associated
     * to a location 
     */
    getWidgetReviewsByLocation$(): Observable<ReviewModel[]> {
        return this.store.select(fromWidgets.getWidgetReviews)
    }

    /**
     * Reviews total getter.
     * Gets the ammount of a particular
     * location's reviews that matches the
     * Widget settings.
     * @returns An observable of the number
     * of total reviews that are associated
     * to a location and matches the widget settings
     */
    getWidgetReviewsTotal$(): Observable<number> {
        return this.store.select(fromWidgets.getWidgetReviewsTotal)
    }

    /**
     * Widget URL getter.
     * Gets the URL that leads to
     * an specific review stack/carousel widget.
     * @returns An observavle of a string
     * that contains the url to the
     * specific review stack/carousel widget.
     */
    getWidgetUrl$(): Observable<string> {
        return this.store.select(fromWidgets.getWidgetUrl)
    }

   /**
     * Reviews by location total getter.
     * Gets the ammount of a particular
     * location's reviews.
     * @returns An observable of the number
     * of total reviews that are associated
     * to a location
     */
    getLoctionReviewsCount$(): Observable<number> {
        return this.store.select(fromWidgets.getWidgetsLoctionReviewsCount)
    }

    /**
     * Location Overall Rating getter.
     * Gets the reviews' average rating 
     * for a given location.
     * @returns An observable of location's
     *  average rating
     */
    getLoctionOverallRating$(): Observable<number> {
        return this.store.select(fromWidgets.getWidgetsLoctionOverallRating)
    }

    /**
     * Location's Generic Landing Page getter.
     * Gets the generic landing page url for a 
     * given location.
     * @returns An observable of a string
     * that contains location's generic 
     * landing page url. 
     */
    getLoctionGenericLanding$(): Observable<string> {
        return this.store.select(fromWidgets.getLoctionGenericLanding)
    }

    /**
     * Location's Stars Count getter.
     * Gets the reviews' stars distribution
     * for a given location.
     * @returns An observable of an array that
     * contains the reviews' stars distribution
     * as follows:
     * <pre 
     * style="color: #333333;
     * background-color: white;
     * font-size:14px;
     * font-family: sans-serif;
     * white-space: pre-wrap;">
     * - Position 0: Reviews' count rated with 1 star
     * - Position 1: Reviews' count rated with 2 stars
     * - Position 2: Reviews' count rated with 3 stars
     * - Position 3: Reviews' count rated with 4 stars
     * - Position 4: Reviews' count rated with 5 stars
     * </pre>
     */
    getLoctionStarsCount$(): Observable<number[]> {
        return this.store.select(fromWidgets.getLoctionStarsCount)
    }

    /**
     * Widget Error getter.
     * Gets the response error to a 
     * widget petition.
     * @returns An observable of a string
     * that contains the associated error.
     */
    getWidgetsError$(): Observable<string> {
        return this.store.select(fromWidgets.getErrorWidgets)
    }

    filterWidgetReviewsByLocation(payload: { startKey: string, startDatePosted: number, widget: WidgetModel, locationId: string }) {
        const request: RequestModel = {
            params: `widgets/?locationId=${payload.locationId}&startKey=${payload.startKey}&startDatePosted=${payload.startDatePosted}`,
            payload: payload.widget
        }
        this.store.dispatch(WidgetsActions.filterReviewsByLocation({ request }))
    }

    /**
     * Dispatches the action to get
     * widget URL
     * <pre>
     * <code class="language-typescript">
     * getWidgetUrl(
        * payload: {
        *  widget: {
        *   bgColor: '#ffffff',
        *   displayMode: 'stack',
        *   filterByRating: [4, 5],
        *   filterByReviewSites: ['5dd6e3b1bade5058faf25cab', '5dd690b6af772b35f90f71e6'],
        *   nameDisplay: 'fullName',
        *   numberOfReviews: 10,
        *   onlyReviewsWithText: true,
        *   reviewsPerPage: 10,
        *   showLeaveReviewButton: false,
        *   showOverallRating: true,
        *  }, 
        *  locationId: '5e87632a56405449d9211b73'
        *  }
        *)
        </code>
     * </pre>
     * @param payload Contains all the properties for
     * the endpoint to return the information, where:
     * <pre 
     * style="color: #333333;
     * background-color: white;
     * font-size:14px;
     * font-family: sans-serif;
     * white-space: pre-wrap;">
     * - widget: contains the settings for the widget.
     * - locationId: is the id of the location for the system to search for the reviews.
     * </pre>
     */
    getWidgetUrl(payload: {widget: WidgetModel, locationId: string}) {
        const request: RequestModel = {
            params: `widgets/url`,
            payload: { ...payload.widget, locationId: payload.locationId }
        }
        this.store.dispatch(WidgetsActions.getWidgetUrl({ request }))
    }

    /**
     * Dispatches the action to search for
     * the selected location reviews ammount
     * <pre>
     * <code class="language-typescript">
     * getLoctionReviewsCount()
        </code>
     * </pre>
     */
    getLoctionReviewsCount() {
        const request: RequestModel = {
            params: `business-report/location/review-count/`
        }
        this.store.dispatch(WidgetsActions.getLoctionReviewsCount({ request }))
    }

    /**
     * Dispatches the action to search for
     * the selected location reviews' stars
     * distribution
     * <pre>
     * <code class="language-typescript">
     * getLoctionStarsCount()
        </code>
     * </pre>
    */
    getLoctionStarsCount() {
        const request: RequestModel = {
            params: `reviews/star-count-location?`
        }
        this.store.dispatch(WidgetsActions.getLoctionStarsCount({ request }))
    }

    /**
     * Dispatches the action to search for
     * the selected location overall reviews
     * rating
     * <pre>
     * <code class="language-typescript">
     * getLoctionOverallRating()
        </code>
     * </pre>
    */
    getLoctionOverallRating() {
        const request: RequestModel = {
            params: `reviews/average-rating-location?`
        }
        this.store.dispatch(WidgetsActions.getLoctionOverallRating({ request }))
    }

    /**
     * Dispatches the action to search for
     * the selected location generic landing
     * page URL
     * <pre>
     * <code class="language-typescript">
     * getLoctionGenericLanding()
        </code>
     * </pre>
    */
    getLoctionGenericLanding() {
        const request: RequestModel = {
            params: `landing/generic-landing-url?`
        }
        this.store.dispatch(WidgetsActions.getLoctionGenericLanding({ request }))
    }

    searchPagesIdWidgets(payload: { reviewRequestFiltered: ReviewRequestFilteredPaginatedModel }) {
        const request: RequestModel = {
            params: `widgets/review-ids-paginated`,
            payload: payload.reviewRequestFiltered
        }
        this.store.dispatch(WidgetsActions.getPagesIdWidgets({ request }))
    }

    getPagesIdWidgets$(): Observable<ReviewPageRequestDescriptor[]> {
        return this.store.select(fromWidgets.getPagesIdWidgets)
    }

}
